import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCountdown } from "../helper/Countdown";
import toast from "react-hot-toast";
import hpProducts from "../data/hp-products.json";
import epsonProducts from "../data/epson-product.json";
import brotherProducts from "../data/brother-products.json";
import canonProducts from "../data/canon-products.json";

const DealsOne = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  // Image resolver
  const getImage = (item) => {
    if (!item) return "/assets/img/placeholder.png";
    if (Array.isArray(item.imageUrl) && item.imageUrl.length > 0)
      return item.imageUrl[0];
    if (typeof item.imageUrl === "string" && item.imageUrl.trim())
      return item.imageUrl;
    return "/assets/img/placeholder.png";
  };

  useEffect(() => {
    setProducts([
      ...brotherProducts,
      ...canonProducts,
      ...hpProducts,
      ...epsonProducts,
    ]);
  }, []);

  // Countdown
  const [timeLeft, setTimeLeft] = useState(getCountdown());
  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getCountdown()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Add to cart
  const handleAddToCart = (product) => {
    const isLoggedIn = sessionStorage.getItem("isLoggedIn");

    if (isLoggedIn !== "true") {
      toast.error("Please login first!");
      navigate("/login-now");
      return;
    }

    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    const existing = cart.find((item) => item.id === product.id);

    if (existing) {
      existing.quantity++;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    sessionStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cart-updated"));
    toast.success(`${product.name} added to cart!`);
  };

  const formatCategory = (str) =>
    str.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  // Add rating + tags
  const productsWithRandomData = useMemo(() => {
    return products.map((item) => ({
      ...item,
      rating: (Math.random() * 1 + 4).toFixed(1),
      reviews: Math.floor(Math.random() * 400) + 100,
      features: ["Wireless", "Fast Print", "Smart Mode", "Eco Saving"]
        .sort(() => 0.5 - Math.random())
        .slice(0, 2),
      formattedCategory: formatCategory(item.category || "Printer"),
    }));
  }, [products]);

  return (
    <section className="new-deals-section">
      <div className="new-container">
        {/* NEW Heading Style */}
        <div className="deals-heading">
          <h2>Top Picks For Everyday Printing</h2>
          <p>
            Explore a curated collection of printers designed for home, office,
            students and small businesses — reliable, fast and affordable.
          </p>
        </div>

        {/* PRODUCT GRID */}
        <div className="new-card-grid">
          {productsWithRandomData.map((item) => (
            <div key={item.id} className="new-card">
              {/* Product Image */}
              <Link to={`/product-details/${item.id}`} className="new-img-wrap">
                <img src={getImage(item)} alt={item.name} />
              </Link>

              {/* CONTENT */}
              <div className="new-card-body">
                <p className="new-category">{item.formattedCategory}</p>

                <Link to={`/product-details/${item.id}`} className="new-title">
                  {item.name}
                </Link>

                <p className="new-desc">
                  {item.description
                    ? item.description.split(" ").slice(0, 10).join(" ") + "..."
                    : "Reliable and high quality printer built for everyday use."}
                </p>

                <div className="new-tags">
                  {item.features.map((tag, i) => (
                    <span key={i} className="new-chip">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Price & Cart */}
                <div className="new-bottom">
                  <h4 className="new-price">${item.price}</h4>

                  <button
                    className="new-cart-btn"
                    onClick={() => handleAddToCart(item)}
                  >
                    Add to Cart
                  </button>
                </div>

                <div className="new-rating">
                  ⭐ {item.rating} <span>({item.reviews} reviews)</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* === STYLING === */}
      <style>{`
        .new-deals-section {
          padding: 70px 0;
          background: #f4f7fb;
        }

        .new-container {
          max-width: 1300px;
          margin: auto;
          padding: 0 20px;
        }

        /* Heading */
        .deals-heading {
          text-align: center;
          margin-bottom: 40px;
        }

        .deals-heading h2 {
          font-size: 32px;
          font-weight: 800;
          color: #1a1a1a;
          margin-bottom: 10px;
        }

        .deals-heading p {
          color: #555;
          font-size: 15px;
          max-width: 700px;
          margin: auto;
          line-height: 1.6;
        }

        /* GRID */
        .new-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
          gap: 26px;
        }

        /* CARD */
        .new-card {
          background: #fff;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 5px 18px rgba(0,0,0,0.07);
          transition: 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .new-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 30px rgba(0,0,0,0.12);
        }

        /* IMAGE */
        .new-img-wrap {
          padding: 28px;
          height: 220px;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #fff;
        }

        .new-img-wrap img {
          width: 72%;
          transition: 0.3s ease;
        }

        .new-card:hover img {
          transform: scale(1.05);
        }

        /* BODY */
        .new-card-body {
          padding: 20px;
          background: #fafafa;
          flex-grow: 1;
        }

        .new-category {
          font-size: 11px;
          color: #777;
          margin-bottom: 4px;
          font-weight: 600;
        }

        .new-title {
          font-size: 18px;
          font-weight: 700;
          color: #111;
          display: block;
          margin-bottom: 8px;
          text-decoration: none;
        }

        .new-desc {
          font-size: 13px;
          color: #666;
          margin-bottom: 16px;
        }

        /* TAGS */
        .new-tags {
          display: flex;
          gap: 8px;
          margin-bottom: 18px;
          flex-wrap: wrap;
        }

        .new-chip {
          background: #e9f1ff;
          color: #0b61d6;
          font-size: 11px;
          padding: 6px 10px;
          border-radius: 8px;
          font-weight: 600;
        }

        /* BOTTOM AREA */
        .new-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .new-price {
          font-size: 22px;
          font-weight: 800;
          color: #0b61d6;
        }

        .new-cart-btn {
          background: #0b61d6;
          color: #fff;
          border: none;
          padding: 10px 18px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          transition: 0.25s;
        }

        .new-cart-btn:hover {
          background: #094eb2;
          transform: translateY(-2px);
        }

        /* RATING */
        .new-rating {
          font-size: 13px;
          color: #444;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }

        .new-rating span {
          color: #777;
        }
      `}</style>
    </section>
  );
};

export default DealsOne;
